import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CreateEpisodeFormValues,
  followUpTaskTypeOptions,
  formatFollowUpType,
} from "../../lib/intakeForm";
import FormControl from "../form/FormControl";
import Input from "../form/Input";

type FollowUpSectionProps = {
  form: UseFormReturn<CreateEpisodeFormValues>;
  stepLabel?: string;
};

function FollowUpSection({
  form,
  stepLabel = "Follow-ups",
}: FollowUpSectionProps) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "followUps",
  });

  return (
    <section className="subCard" aria-labelledby="follow-up-heading">
      <div className="cardHeaderRow">
        <h3 id="follow-up-heading" className="subCardTitle">
          {stepLabel}
        </h3>

        <button
          className="secondaryButton"
          id="add-follow-up"
          onClick={() =>
            append({
              taskType: "",
              dueAt: "",
            })
          }
          type="button"
        >
          {t("Add follow-up")}
        </button>
      </div>

      {fields.length === 0 ? (
        <p className="hintText sectionHint">
          {t("Add one or more follow-up tasks if needed.")}
        </p>
      ) : null}

      {fields.map((field, index) => {
        const taskTypeName = `followUps.${index}.taskType` as const;
        const dueAtName = `followUps.${index}.dueAt` as const;

        return (
          <div className="followUpCard" key={field.id}>
            <div className="followUpCardHeader">
              <p className="followUpTitle">
                {t("Follow-up {{index}}", { index: index + 1 })}
              </p>
              <button
                aria-label={t("Remove follow-up {{index}}", {
                  index: index + 1,
                })}
                className="secondaryButton"
                onClick={() => remove(index)}
                type="button"
              >
                {t("Remove")}
              </button>
            </div>

            <div className="formColumns">
              <FormControl<CreateEpisodeFormValues>
                label="Task type"
                name={taskTypeName}
              >
                <select
                  className="inputControl"
                  {...form.register(taskTypeName, {
                    validate: (value) =>
                      (typeof value === "string" && value.trim().length > 0) ||
                      t("Task type is required"),
                  })}
                >
                  <option value="">{t("Select task type")}</option>
                  {followUpTaskTypeOptions.map((taskType) => (
                    <option key={taskType} value={taskType}>
                      {t(formatFollowUpType(taskType))}
                    </option>
                  ))}
                </select>
              </FormControl>

              <FormControl<CreateEpisodeFormValues> label="Due at" name={dueAtName}>
                <Input<CreateEpisodeFormValues>
                  name={dueAtName}
                  rules={{
                    validate: (value) =>
                      (typeof value === "string" && value.trim().length > 0) ||
                      t("Due date and time is required"),
                  }}
                  type="datetime-local"
                />
              </FormControl>
            </div>
          </div>
        );
      })}
    </section>
  );
}

export default FollowUpSection;
